import { useState, useEffect, useCallback } from 'react';
import { useChat } from '../../context/ChatContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { FormulaTooltip, resolveMetricKey } from '../FormulaTooltip';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  AreaChart, Area, BarChart, Bar, LineChart, Line, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend, 
  PieChart, Pie, Treemap
} from 'recharts';
import { 
  Users,
  User,
  UserCheck,
  UserX,
  Wallet,
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  AlertTriangle,
  Repeat,
  CreditCard,
  Calendar,
  Filter,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Download,
  Sparkles,
  Search,
  Layers,
  PieChart as PieChartIcon,
  BarChart3,
  Eye,
  Clock,
  MapPin,
  Store,
  Zap,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';
import axios from 'axios';
import DrillDownModal from '../DrillDownModal';
import DashboardFilters from '../DashboardFilters';
import { formatNumber, formatCurrency } from '../../lib/formatters';
import { ExportButton } from '../ExportUtils';
import { AskAIButton } from '../AskAIButton';

import { API_BASE_URL as API } from '../../utils/apiConfig';

// Segment Card Component
const SegmentCard = ({ segment, onClick, isSelected }) => {
  const getColorByRevenue = (pct) => {
    if (pct >= 20) return 'from-emerald-500/20 to-teal-500/10 border-emerald-500/30';
    if (pct >= 10) return 'from-blue-500/20 to-cyan-500/10 border-blue-500/30';
    if (pct >= 5) return 'from-violet-500/20 to-purple-500/10 border-violet-500/30';
    return 'from-slate-500/20 to-gray-500/10 border-slate-500/30';
  };

  return (
    <Card 
      className={`cursor-pointer hover:shadow-lg transition-all bg-gradient-to-br ${getColorByRevenue(segment.revenue_pct)} ${isSelected ? 'ring-2 ring-primary' : ''}`}
      onClick={() => onClick(segment)}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-sm truncate flex-1" title={segment.name}>
            {segment.name}
          </h4>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">Customers</p>
            <p className="font-semibold">{segment.count?.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Revenue</p>
            <p className="font-semibold">{formatCurrency(segment.revenue)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Avg ATV</p>
            <p className="font-medium">₹{segment.avg_atv?.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">% of Total</p>
            <p className="font-medium">{segment.pct_of_total}%</p>
          </div>
        </div>
        <Progress value={segment.revenue_pct} className="mt-2 h-1.5" />
      </CardContent>
    </Card>
  );
};

// Segment Tree Node Component
const SegmentTreeNode = ({ node, segments, level = 0, onSelect, expandedNodes, toggleNode }) => {
  const isExpanded = expandedNodes.includes(node.segment_key || node.name);
  const segmentData = node.segment_key ? segments[node.segment_key] : null;
  const hasChildren = node.children && Object.keys(node.children).length > 0;

  const formatCurrency = (val) => {
    if (!val) return '₹0';
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
    return `₹${Math.round(val).toLocaleString()}`;
  };

  return (
    <div className="border-l border-border/50" style={{ marginLeft: level > 0 ? '16px' : '0' }}>
      <div 
        className={`flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 cursor-pointer ${level === 0 ? 'bg-muted/30' : ''}`}
        onClick={() => {
          if (hasChildren) toggleNode(node.segment_key || node.name);
          if (segmentData) onSelect(segmentData, node.segment_key);
        }}
      >
        {hasChildren ? (
          isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
        ) : (
          <div className="w-4" />
        )}
        <span className={`flex-1 ${level === 0 ? 'font-semibold' : 'text-sm'}`}>
          {node.name}
        </span>
        {segmentData && (
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>{segmentData.count?.toLocaleString()} customers</span>
            <span>{formatCurrency(segmentData.revenue)}</span>
            <Badge variant="outline" className="text-xs">{segmentData.pct_of_total}%</Badge>
          </div>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div className="ml-2">
          {Object.entries(node.children).map(([key, child]) => (
            <SegmentTreeNode 
              key={key}
              node={child}
              segments={segments}
              level={level + 1}
              onSelect={onSelect}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Insight Card Component
const InsightCard = ({ insight }) => {
  const severityConfig = {
    success: { bg: 'from-emerald-500/10 to-teal-500/5', border: 'border-emerald-500/20', icon: CheckCircle, color: 'text-emerald-400' },
    warning: { bg: 'from-amber-500/10 to-orange-500/5', border: 'border-amber-500/20', icon: AlertTriangle, color: 'text-amber-400' },
    critical: { bg: 'from-red-500/10 to-pink-500/5', border: 'border-red-500/20', icon: AlertCircle, color: 'text-red-400' },
    info: { bg: 'from-blue-500/10 to-cyan-500/5', border: 'border-blue-500/20', icon: Info, color: 'text-blue-400' }
  };

  const config = severityConfig[insight.severity] || severityConfig.info;
  const Icon = config.icon;

  return (
    <Card className={`bg-gradient-to-br ${config.bg} ${config.border}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-background/50`}>
            <Icon className={`w-5 h-5 ${config.color}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-sm">{insight.title}</h4>
              <Badge variant="outline" className={`text-xs ${insight.priority === 'high' ? 'bg-red-500/10 text-red-400' : ''}`}>
                {insight.priority?.toUpperCase()}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mb-2">{insight.insight}</p>
            <div className="flex items-center justify-between text-xs">
              <span className="text-primary">💡 {insight.recommendation}</span>
              <Badge variant="secondary">{insight.impact}</Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Single Customer View Component
const SingleCustomerView = ({ customer, onBack }) => {
  const formatCurrency = (val) => {
    if (!val) return '₹0';
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
    return `₹${Math.round(val).toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: 'numeric', month: 'short', year: 'numeric' 
    });
  };

  // Map API response structure to component expectations
  const profile = {
    name: customer.customer?.name || 'Customer',
    mobile: customer.customer?.mobile || '',
    city: customer.customer?.city || '',
    tier: customer.customer?.tier || 'Member',
    ltv: customer.metrics?.lifetime_value || 0,
    total_visits: customer.metrics?.total_visits || 0,
    atv: customer.metrics?.atv || 0,
    recency_days: customer.metrics?.recency_days || 0
  };
  const kpis = customer.kpis || [];
  const timeline = customer.spend_timeline || [];
  const categoryMix = customer.category_mix || [];
  const pointsWallet = {
    balance: customer.points_wallet?.balance || 0,
    total_earned: customer.points_wallet?.earned || 0,
    total_redeemed: customer.points_wallet?.redeemed || 0
  };
  const recommendations = customer.next_best_actions || [];
  const rfm = customer.rfm || {};
  const churnRisk = customer.churn?.risk_pct || 0;
  const churnFactors = customer.churn?.factors || [];

  return (
    <div className="space-y-6" data-testid="customer-360-view">
      {/* Back Button & Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
          <ChevronRight className="w-4 h-4 rotate-180" />
          Back to Search
        </Button>
      </div>

      {/* Customer Profile Header */}
      <Card className="bg-gradient-to-br from-violet-500/10 via-purple-500/5 to-pink-500/10 border-violet-500/20">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                {profile.name?.charAt(0) || 'C'}
              </div>
              <div>
                <h2 className="text-xl font-bold">{profile.name || 'Customer'}</h2>
                <p className="text-muted-foreground flex items-center gap-2">
                  <span>{profile.mobile}</span>
                  {profile.city && <><span>•</span><MapPin className="w-3 h-3" /><span>{profile.city}</span></>}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary">{profile.tier || 'Member'}</Badge>
                  <Badge variant={rfm.segment?.includes('Champion') ? 'default' : 'outline'} className="bg-gradient-to-r from-violet-500/20 to-purple-500/20">
                    {rfm.segment || 'New Customer'}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-500">{formatCurrency(profile.ltv)}</p>
                <p className="text-xs text-muted-foreground">Lifetime Value</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-500">{profile.total_visits || 0}</p>
                <p className="text-xs text-muted-foreground">Total Visits</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-violet-500">{formatCurrency(profile.atv)}</p>
                <p className="text-xs text-muted-foreground">Avg. Ticket</p>
              </div>
              <div className="text-center">
                <p className={`text-2xl font-bold ${profile.recency_days <= 30 ? 'text-emerald-500' : profile.recency_days <= 90 ? 'text-amber-500' : 'text-red-500'}`}>
                  {profile.recency_days || 0}d
                </p>
                <p className="text-xs text-muted-foreground">Since Last Visit</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {kpis.map((kpi, idx) => (
          <Card key={idx} className="hover:shadow-md transition-shadow" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${kpi.color}20` }}>
                  <Activity className="w-4 h-4" style={{ color: kpi.color }} />
                </div>
                <FormulaTooltip metric={resolveMetricKey(kpi.label)} name={kpi.label}>
                  <span className="text-xs text-muted-foreground truncate">{kpi.label}</span>
                </FormulaTooltip>
              </div>
              <p className="text-lg font-bold">{kpi.formatted}</p>
              {kpi.subtext && <p className="text-xs text-muted-foreground">{kpi.subtext}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Spend Timeline */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              Spend Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            {timeline.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                  <Bar yAxisId="left" dataKey="spend" name="Spend" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="visits" name="Visits" stroke="#10B981" strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No timeline data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Mix */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PieChartIcon className="w-4 h-4 text-pink-500" />
              Category Mix
            </CardTitle>
          </CardHeader>
          <CardContent>
            {categoryMix.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={categoryMix}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {categoryMix.map((entry, index) => (
                      <Cell key={index} fill={['#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#3B82F6', '#6366F1'][index % 6]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No category data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Points Wallet & RFM Score */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Points Wallet */}
        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Wallet className="w-4 h-4 text-amber-500" />
              Points Wallet
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Current Balance</span>
              <span className="text-2xl font-bold text-amber-500">{pointsWallet.balance?.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Total Earned</span>
              <span className="text-emerald-500">+{pointsWallet.total_earned?.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Total Redeemed</span>
              <span className="text-red-500">-{pointsWallet.total_redeemed?.toLocaleString() || 0}</span>
            </div>
            <Progress value={(pointsWallet.total_redeemed / (pointsWallet.total_earned || 1)) * 100} className="h-2" />
            <p className="text-xs text-muted-foreground text-center">
              {Math.round((pointsWallet.total_redeemed / (pointsWallet.total_earned || 1)) * 100)}% redemption rate
            </p>
          </CardContent>
        </Card>

        {/* RFM Score */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="w-4 h-4 text-violet-500" />
              RFM Score
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-center mb-4">
              <Badge className="text-lg px-4 py-1 bg-gradient-to-r from-violet-500 to-purple-600">
                {rfm.r_score || 0}-{rfm.f_score || 0}-{rfm.m_score || 0}
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Recency (R)</span>
                <div className="flex items-center gap-2">
                  <Progress value={(rfm.r_score || 0) * 20} className="w-24 h-2" />
                  <span className="text-sm font-medium w-6">{rfm.r_score || 0}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Frequency (F)</span>
                <div className="flex items-center gap-2">
                  <Progress value={(rfm.f_score || 0) * 20} className="w-24 h-2" />
                  <span className="text-sm font-medium w-6">{rfm.f_score || 0}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Monetary (M)</span>
                <div className="flex items-center gap-2">
                  <Progress value={(rfm.m_score || 0) * 20} className="w-24 h-2" />
                  <span className="text-sm font-medium w-6">{rfm.m_score || 0}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Churn Risk */}
        <Card className={`${customer.churn_risk_pct >= 70 ? 'bg-red-500/10 border-red-500/20' : customer.churn_risk_pct >= 40 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-emerald-500/10 border-emerald-500/20'}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Churn Risk Assessment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <p className={`text-4xl font-bold ${customer.churn_risk_pct >= 70 ? 'text-red-500' : customer.churn_risk_pct >= 40 ? 'text-amber-500' : 'text-emerald-500'}`}>
                {customer.churn_risk_pct || 0}%
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {customer.churn_risk_pct >= 70 ? 'High Risk - Immediate action needed' : 
                 customer.churn_risk_pct >= 40 ? 'Medium Risk - Monitor closely' : 
                 'Low Risk - Engaged customer'}
              </p>
            </div>
            <div className="mt-4 space-y-2">
              {customer.churn_factors?.slice(0, 3).map((factor, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  <div className={`w-2 h-2 rounded-full ${factor.impact === 'high' ? 'bg-red-500' : 'bg-amber-500'}`} />
                  <span className="text-muted-foreground">{factor.reason}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-pink-500" />
              Recommended Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recommendations.map((rec, idx) => (
                <div key={idx} className="p-4 rounded-lg bg-muted/50 border">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-pink-500/20 to-violet-500/20">
                      <Zap className="w-4 h-4 text-pink-500" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{rec.action}</h4>
                      <p className="text-xs text-muted-foreground mt-1">{rec.reason}</p>
                      <Badge variant="outline" className="mt-2 text-xs">{rec.expected_impact}</Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default function CustomerDashboard() {
  const { selectedBrand } = useChat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('overview'); // overview, segments, tree, insights, customer360
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(['One Timer Customers', 'Repeat Customers']);
  const [searchTerm, setSearchTerm] = useState('');
  const [segmentFilters, setSegmentFilters] = useState({
    date_range: 'lifetime',
    segment_type: 'All',
    recency: 'All',
    visits: 'All'
  });
  const [filters, setFilters] = useState({ dateRange: 'lifetime', city: 'all', store: 'all' });
  const [filterOptions, setFilterOptions] = useState({ cities: [], stores: [] });
  const [drillDown, setDrillDown] = useState({ open: false, type: null, data: null, title: '' });
  
  // Customer 360 State
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [isLiveData, setIsLiveData] = useState(false);

  // Check if brand has live data
  const getDbBrandId = useCallback(() => {
    return selectedBrand?.db_brand_id || selectedBrand?.settings?.db_brand_id || null;
  }, [selectedBrand]);

  const fetchData = useCallback(async () => {
    if (!selectedBrand) return;
    
    setLoading(true);
    setError(null);
    
    // Check for live Data Bridge - mark as live if brand has db_brand_id
    const dbBrandId = getDbBrandId();
    // Live status set after response
    
    // Use original API for proper data structure
    try {
      const response = await axios.post(
        `${API}/api/analytics/customer-segmentation`,
        { brand_id: selectedBrand.brand_id, filters },
        { withCredentials: true }
      );
      setData(response.data.data);
      setIsLiveData(response.data.data?.source === 'live_data_bridge' || !!dbBrandId);
      
      // Extract filter options from response
      if (response.data.data?.filter_context) {
        setFilterOptions({
          cities: response.data.data.filter_context.cities || [],
          stores: response.data.data.filter_context.stores || []
        });
      } else if (response.data.data?.filterOptions) {
        setFilterOptions({
          cities: response.data.data.filterOptions.cities || [],
          stores: response.data.data.filterOptions.stores || []
        });
      }
    } catch (err) {
      console.error('Customer segmentation error:', err);
      setError('Failed to load customer segmentation data');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, filters, getDbBrandId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = () => {
    if (!data) return;
    const csvContent = [
      ['Segment', 'Customers', 'Revenue', 'Avg ATV', '% of Total'],
      ...(data.top_segments || []).map(s => [s.name, s.count, s.revenue, s.avg_atv, s.pct_of_total])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer_segmentation_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Customer 360 - Search customers
  const searchCustomers = useCallback(async (term) => {
    if (!selectedBrand) return;
    
    setCustomerSearchLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/analytics/consumer-360`,
        { 
          brand_id: selectedBrand.brand_id, 
          filters: { search: term }
        },
        { withCredentials: true }
      );
      setCustomerSearchResults(response.data.data?.customers || []);
    } catch (err) {
      console.error('Customer search error:', err);
      setCustomerSearchResults([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  }, [selectedBrand]);

  // Load recent customers when switching to customer360 tab
  useEffect(() => {
    if (activeView === 'customer360' && !selectedCustomer && customerSearchResults.length === 0) {
      searchCustomers('');
    }
  }, [activeView, selectedCustomer, customerSearchResults.length, searchCustomers]);

  // Customer 360 - Load single customer details
  const loadCustomerDetails = useCallback(async (customerId) => {
    if (!selectedBrand || !customerId) return;
    
    // Set selected customer FIRST to trigger loading UI
    setSelectedCustomer(customerId);
    setCustomerLoading(true);
    setCustomerData(null);
    
    try {
      const response = await axios.post(
        `${API}/api/analytics/consumer-360`,
        { 
          brand_id: selectedBrand.brand_id, 
          customer_id: customerId,
          filters: { date_range: filters.date_range }
        },
        { withCredentials: true }
      );
      
      if (response.data.data?.error) {
        console.error('Customer details error:', response.data.data.error);
        setCustomerData(null);
        setSelectedCustomer(null);
      } else {
        setCustomerData(response.data.data);
      }
    } catch (err) {
      console.error('Customer details error:', err);
      setCustomerData(null);
      setSelectedCustomer(null);
    } finally {
      setCustomerLoading(false);
    }
  }, [selectedBrand, filters.date_range]);

  // Handle customer search input with debounce
  useEffect(() => {
    if (activeView !== 'customer360') return;
    
    const timer = setTimeout(() => {
      if (customerSearchTerm) {
        searchCustomers(customerSearchTerm);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [customerSearchTerm, activeView, searchCustomers]);

  const toggleNode = (nodeKey) => {
    setExpandedNodes(prev => 
      prev.includes(nodeKey) 
        ? prev.filter(n => n !== nodeKey)
        : [...prev, nodeKey]
    );
  };

  const handleSegmentSelect = (segment, key) => {
    setSelectedSegment({ ...segment, key });
    setDrillDown({
      open: true,
      type: 'segment',
      data: { value: key, context: segment },
      title: segment.name
    });
  };

  const formatCurrency = (val) => {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
    return `₹${Math.round(val || 0).toLocaleString()}`;
  };

  // Use imported formatNumber from lib/formatters

  const KPI_ICONS = {
    'users': Users,
    'activity': Activity,
    'user': User,
    'repeat': Repeat,
    'wallet': Wallet,
    'trending-up': TrendingUp,
    'credit-card': CreditCard,
    'alert-triangle': AlertTriangle
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="customer-segmentation-loading">
        <div className="flex flex-col items-center gap-4">
          <Users className="w-16 h-16 text-violet-500 animate-pulse" />
          <p className="text-muted-foreground">Analyzing customer segments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={fetchData}>Retry</Button>
        </Card>
      </div>
    );
  }

  const kpis = data?.kpis || [];
  const segments = data?.segments || {};
  const segmentTree = data?.segment_tree || {};
  const topSegments = data?.top_segments || [];
  const trend = data?.trend || [];
  const churnDistribution = data?.churn_distribution || {};
  const insights = data?.insights || [];
  const segmentFilterOptions = data?.filters || {};
  const benchmarks = data?.benchmarks || {};

  // Filter segments based on search and filters
  const filteredSegments = Object.entries(segments).filter(([key, seg]) => {
    if (searchTerm && !seg.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (segmentFilters.segment_type !== 'All') {
      if (segmentFilters.segment_type === 'One Timer' && !key.includes('one_timer')) return false;
      if (segmentFilters.segment_type === 'Repeat' && !key.includes('repeat')) return false;
      if (segmentFilters.segment_type === 'Above ATV' && !key.includes('above_atv')) return false;
      if (segmentFilters.segment_type === 'Below ATV' && !key.includes('below_atv')) return false;
      if (segmentFilters.segment_type === 'Dormant' && !key.includes('not_visited')) return false;
    }
    return seg.count > 0;
  });

  return (
    <div className="space-y-6" data-testid="customer-segmentation-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-cyan-500 via-blue-500 to-violet-500 shadow-lg shadow-blue-500/30">
              <Users className="w-6 h-6 text-white" />
            </div>
            Customer Segmentation
            {data?.data_as_of && (
              <Badge className="ml-2 bg-blue-500/80 text-white">
                <Clock className="w-3 h-3 mr-1" />
                Data as of: {data.data_as_of}
              </Badge>
            )}
          </h2>
          <p className="text-muted-foreground">Detailed customer segments with AI insights & drill-down</p>
        </div>
      </div>

      {/* Dashboard Filters */}
      <DashboardFilters
        filters={filters}
        setFilters={setFilters}
        filterOptions={filterOptions}
        onRefresh={fetchData}
        onExport={handleExport}
        loading={loading}
      />

      {/* Segment-specific Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Date Range */}
        <Select 
          value={segmentFilters.date_range} 
          onValueChange={(v) => setSegmentFilters(prev => ({ ...prev, date_range: v }))}
        >
          <SelectTrigger className="w-28" data-testid="segment-date-range">
            <Calendar className="w-4 h-4 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {segmentFilterOptions.date_ranges?.map(r => (
              <SelectItem key={r} value={r}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Segment Type Filter */}
        <Select 
          value={segmentFilters.segment_type} 
          onValueChange={(v) => setSegmentFilters(prev => ({ ...prev, segment_type: v }))}
        >
          <SelectTrigger className="w-36" data-testid="segment-type-filter">
            <Filter className="w-4 h-4 mr-1" />
            <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {segmentFilterOptions.segment_types?.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
        {/* Full Report Export */}
        <ExportButton 
          brandId={selectedBrand?.brand_id}
          dashboardType="customer_segmentation"
          label="Download Full Report"
          filename={`customer_segments_${selectedBrand?.name || 'export'}.csv`}
        />
        </div>

      {/* View Tabs */}
      <div className="flex items-center gap-2 bg-muted p-1 rounded-lg w-fit flex-wrap">
        {[
          { id: 'overview', label: 'Overview', icon: Eye },
          { id: 'segments', label: 'All Segments', icon: Layers },
          { id: 'tree', label: 'Segment Tree', icon: BarChart3 },
          { id: 'insights', label: 'AI Insights', icon: Sparkles },
          { id: 'customer360', label: 'Individual Customer', icon: User }
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeView === tab.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => {
              setActiveView(tab.id);
              if (tab.id !== 'customer360') {
                setSelectedCustomer(null);
                setCustomerData(null);
              }
            }}
            className="gap-2"
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* KPI Row - Hide for Customer 360 view */}
      {activeView !== 'customer360' && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          {kpis.map((kpi, idx) => {
            const Icon = KPI_ICONS[kpi.icon] || Users;
            return (
              <Card 
                key={idx}
                className="cursor-pointer hover:shadow-lg transition-all group relative overflow-hidden"
                onClick={() => setDrillDown({
                  open: true,
                  type: 'kpi',
                  data: { value: kpi.id },
                  title: kpi.label
                })}
              >
                <div 
                  className="absolute inset-0 opacity-10" 
                  style={{ background: `linear-gradient(135deg, ${kpi.color}40, transparent)` }}
                />
                <CardContent className="p-3 relative">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4" style={{ color: kpi.color }} />
                    <FormulaTooltip metric={resolveMetricKey(kpi.label)} name={kpi.label}>
                      <span className="text-xs text-muted-foreground truncate">{kpi.label}</span>
                    </FormulaTooltip>
                  </div>
                  <p className="text-lg font-bold">{kpi.formatted}</p>
                  {kpi.subtext && (
                    <p className="text-xs text-muted-foreground">{kpi.subtext}</p>
                  )}
                  <ChevronRight className="w-4 h-4 absolute bottom-2 right-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Content Based on View */}
      {activeView === 'overview' && (
        <>
          {/* One Timer vs Repeat Summary */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* One Timer Summary */}
            <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-5 h-5 text-amber-500" />
                  One Timer Customers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-2xl font-bold">{formatNumber(segments.one_timer_overall?.count)}</p>
                    <p className="text-xs text-muted-foreground">Total</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatCurrency(segments.one_timer_overall?.revenue)}</p>
                    <p className="text-xs text-muted-foreground">Revenue</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{segments.one_timer_overall?.pct_of_total}%</p>
                    <p className="text-xs text-muted-foreground">of Base</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Above ATV</span>
                    <span className="font-medium">{formatNumber(segments.one_timer_above_atv?.count)}</span>
                  </div>
                  <Progress value={(segments.one_timer_above_atv?.count / segments.one_timer_overall?.count) * 100 || 0} className="h-2" />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Below ATV</span>
                    <span className="font-medium">{formatNumber(segments.one_timer_below_atv?.count)}</span>
                  </div>
                  <Progress value={(segments.one_timer_below_atv?.count / segments.one_timer_overall?.count) * 100 || 0} className="h-2 [&>div]:bg-amber-500" />
                </div>
              </CardContent>
            </Card>

            {/* Repeat Summary */}
            <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border-emerald-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Repeat className="w-5 h-5 text-emerald-500" />
                  Repeat Customers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-2xl font-bold">{formatNumber(segments.repeat_overall?.count)}</p>
                    <p className="text-xs text-muted-foreground">Total</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatCurrency(segments.repeat_overall?.revenue)}</p>
                    <p className="text-xs text-muted-foreground">Revenue</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{segments.repeat_overall?.revenue_pct}%</p>
                    <p className="text-xs text-muted-foreground">of Revenue</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Above ATV</span>
                    <span className="font-medium">{formatNumber(segments.repeat_above_atv_overall?.count)}</span>
                  </div>
                  <Progress value={(segments.repeat_above_atv_overall?.count / segments.repeat_overall?.count) * 100 || 0} className="h-2 [&>div]:bg-emerald-500" />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Below ATV</span>
                    <span className="font-medium">{formatNumber(segments.repeat_below_atv_overall?.count)}</span>
                  </div>
                  <Progress value={(segments.repeat_below_atv_overall?.count / segments.repeat_overall?.count) * 100 || 0} className="h-2 [&>div]:bg-teal-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Segments & Trend */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Top Segments by Revenue */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-violet-500" />
                  Top Segments by Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topSegments.slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 10 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => formatCurrency(value)}
                    />
                    <Bar dataKey="revenue" fill="#8B5CF6" radius={[0, 4, 4, 0]}>
                      {topSegments.slice(0, 10).map((entry, index) => (
                        <Cell key={index} fill={['#8B5CF6', '#EC4899', '#14B8A6', '#F59E0B', '#3B82F6', '#6366F1', '#10B981', '#EF4444', '#F97316', '#06B6D4'][index % 10]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Churn Risk Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  Churn Risk Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Low (0-90D)', value: churnDistribution.low, fill: '#10B981' },
                        { name: 'Medium (91-180D)', value: churnDistribution.medium, fill: '#F59E0B' },
                        { name: 'High (181-365D)', value: churnDistribution.high, fill: '#F97316' },
                        { name: 'Critical (365D+)', value: churnDistribution.critical, fill: '#EF4444' }
                      ]}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                    >
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span>Low: {churnDistribution.low?.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                    <span>Medium: {churnDistribution.medium?.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-orange-500" />
                    <span>High: {churnDistribution.high?.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <span>Critical: {churnDistribution.critical?.toLocaleString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Customer Trend */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-blue-500" />
                Monthly Customer Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="one_timers" name="One Timers" fill="#F59E0B" stackId="customers" />
                  <Bar yAxisId="left" dataKey="repeat" name="Repeat" fill="#10B981" stackId="customers" />
                  <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#8B5CF6" strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Quick Insights */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {insights.slice(0, 3).map((insight, idx) => (
              <InsightCard key={idx} insight={insight} />
            ))}
          </div>
        </>
      )}

      {activeView === 'segments' && (
        <>
          {/* Search */}
          <Card className="bg-muted/30">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search segments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardContent>
          </Card>

          {/* Segment Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredSegments.map(([key, seg]) => (
              <SegmentCard 
                key={key}
                segment={{ ...seg, key }}
                onClick={(s) => handleSegmentSelect(s, key)}
                isSelected={selectedSegment?.key === key}
              />
            ))}
          </div>

          {filteredSegments.length === 0 && (
            <Card className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No segments match your filters</p>
            </Card>
          )}
        </>
      )}

      {activeView === 'tree' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Layers className="w-5 h-5 text-violet-500" />
              Segment Hierarchy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(segmentTree).map(([key, node]) => (
                <SegmentTreeNode
                  key={key}
                  node={node}
                  segments={segments}
                  onSelect={handleSegmentSelect}
                  expandedNodes={expandedNodes}
                  toggleNode={toggleNode}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {activeView === 'insights' && (
        <div className="grid md:grid-cols-2 gap-4">
          {insights.map((insight, idx) => (
            <InsightCard key={idx} insight={insight} />
          ))}
        </div>
      )}

      {/* Customer 360 View */}
      {activeView === 'customer360' && (
        <>
          {selectedCustomer ? (
            // Show loading or single customer details
            customerLoading ? (
              <div className="flex items-center justify-center h-96">
                <div className="flex flex-col items-center gap-4">
                  <User className="w-16 h-16 text-violet-500 animate-pulse" />
                  <p className="text-muted-foreground">Loading customer details...</p>
                </div>
              </div>
            ) : customerData ? (
              <SingleCustomerView 
                customer={customerData} 
                onBack={() => {
                  setSelectedCustomer(null);
                  setCustomerData(null);
                }}
              />
            ) : (
              // Error state - customer not found
              <div className="flex flex-col items-center justify-center h-96 gap-4">
                <AlertCircle className="w-16 h-16 text-red-500" />
                <p className="text-muted-foreground">Customer not found or error loading details</p>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setSelectedCustomer(null);
                    setCustomerData(null);
                  }}
                >
                  Back to Search
                </Button>
              </div>
            )
          ) : (
            // Show search and customer list
            <div className="space-y-6" data-testid="customer-search-section">
              {/* Search Box */}
              <Card className="bg-gradient-to-br from-violet-500/5 to-purple-500/5 border-violet-500/20">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        placeholder="Search by mobile number or customer name..."
                        value={customerSearchTerm}
                        onChange={(e) => setCustomerSearchTerm(e.target.value)}
                        className="pl-11 h-12 text-base"
                        data-testid="customer-search-input"
                      />
                    </div>
                    <Button 
                      onClick={() => searchCustomers(customerSearchTerm)} 
                      className="h-12 px-6 gap-2"
                      disabled={customerSearchLoading}
                    >
                      {customerSearchLoading ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                      Search
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">
                    Search for a specific customer to view their complete 360° profile including spend patterns, loyalty status, and personalized recommendations.
                  </p>
                </CardContent>
              </Card>

              {/* Recent / Search Results */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    {customerSearchTerm ? 'Search Results' : 'Recent Customers'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {customerSearchLoading ? (
                    <div className="flex items-center justify-center h-48">
                      <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : customerSearchResults.length > 0 ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {customerSearchResults.map((customer, idx) => (
                        <Card 
                          key={idx}
                          className="cursor-pointer hover:shadow-lg hover:border-primary/50 transition-all group"
                          onClick={() => loadCustomerDetails(customer.mobile)}
                          data-testid={`customer-card-${customer.mobile}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-medium">
                                  {customer.name?.charAt(0) || 'C'}
                                </div>
                                <div>
                                  <h4 className="font-medium">{customer.name || 'Unknown'}</h4>
                                  <p className="text-sm text-muted-foreground">{customer.mobile}</p>
                                </div>
                              </div>
                              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                            <div className="mt-3 flex items-center justify-between text-sm">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <MapPin className="w-3 h-3" />
                                {customer.city || 'N/A'}
                              </div>
                              {customer.tier && (
                                <Badge variant="secondary" className="text-xs">{customer.tier}</Badge>
                              )}
                            </div>
                            {customer.ltv && (
                              <div className="mt-2 flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">LTV</span>
                                <span className="font-semibold text-emerald-500">₹{customer.ltv?.toLocaleString()}</span>
                              </div>
                            )}
                            {customer.visits && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Visits</span>
                                <span className="font-medium">{customer.visits}</span>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <User className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-muted-foreground">
                        {customerSearchTerm ? 'No customers found matching your search' : 'No recent customers found'}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Benchmark Footer - Hide for Customer 360 view */}
      {activeView !== 'customer360' && (
        <Card className="bg-muted/30">
          <CardContent className="p-4 flex items-center justify-between text-sm flex-wrap gap-4">
            <div className="flex items-center gap-6 flex-wrap">
              <div>
                <span className="text-muted-foreground">ATV Benchmark:</span>
                <span className="ml-2 font-semibold">₹{benchmarks.overall_atv?.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Repeat Ratio Benchmark:</span>
                <span className="ml-2 font-semibold">{benchmarks.repeat_ratio_benchmark}%</span>
              </div>
              <div>
                <span className="text-muted-foreground">Active Rate Benchmark:</span>
                <span className="ml-2 font-semibold">{benchmarks.active_rate_benchmark}%</span>
              </div>
            </div>
            <Badge variant="outline">Based on industry standards</Badge>
          </CardContent>
        </Card>
      )}

      {/* Drill Down Modal */}
      <DrillDownModal
        open={drillDown.open}
        onClose={() => setDrillDown({ open: false, type: null, data: null, title: '' })}
        drillType={drillDown.type}
        drillData={drillDown.data}
        title={drillDown.title}
        brandId={selectedBrand?.brand_id}
      />
    </div>
  );
}
